import {Button} from '@wandb/components';
import {Checkbox} from '@wandb/weave/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import {Icon} from '@wandb/weave/components/Icon';
import {
  maybePluralize,
  maybePluralizeWord,
} from '@wandb/weave/core/util/string';
import React, {memo, useState} from 'react';

import {Banner, BannerFlexWrapper, BannerTextWrapper} from './Banner';
import {useRunSelectorAnalyticsLocation} from './RunSelector/RunSelectorAnalyticsLocationContext';

interface DeleteRunsButtonProps {
  selectedCount: number;
  outputArtifactsCount: number;
  activeRunsCount: number;
  onDelete(deleteArtifacts: boolean): void;
}

const DeleteRunsButtonComp: React.FC<DeleteRunsButtonProps> = ({
  selectedCount,
  onDelete,
  outputArtifactsCount,
  activeRunsCount,
}) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const [deleteArtifacts, setDeleteArtifacts] = useState(false);
  const [open, setOpen] = useState(false);
  const onOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    setDeleteArtifacts(false);
  };

  const isDisabled = selectedCount === 0;
  if (isDisabled) {
    return null;
  }

  const button = (
    <Button
      data-test="run-delete-popup"
      startIcon="delete"
      isActive={open}
      isDisabled={isDisabled}
      variant="secondary"
      aria-label="Delete selected runs"
      tooltip={{children: 'Delete from project'}}
      onPress={() => {
        if (selectedCount !== 0) {
          setOpen(true);
          window.analytics?.track('Delete run clicked', {
            location: analyticsLocation,
          });
        }
      }}>
      Delete
    </Button>
  );

  const showCheckbox = outputArtifactsCount > 0;

  return (
    <React.Fragment>
      {button}
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay />
          <Dialog.Content
            className="w-[540px] rounded-lg"
            onInteractOutside={e => {
              e.preventDefault();
            }}>
            <Dialog.Title>
              Delete {maybePluralizeWord(selectedCount, 'run')}?
            </Dialog.Title>
            <Dialog.Description className="mt-16">
              {activeRunsCount > 0 ? (
                <span>
                  You’re deleting{' '}
                  {maybePluralize(activeRunsCount, 'active run')}, which might
                  crash live processes if your run is actually still running. We
                  suggest that you stop any runs before deleting them.
                </span>
              ) : (
                <span>
                  Delete {maybePluralize(selectedCount, 'run')}? This affects
                  all pages, including reports.{' '}
                </span>
              )}
            </Dialog.Description>
            <Dialog.Description className="mt-16">
              {showCheckbox && (
                <div>
                  <div className="flex gap-8">
                    <div className="mb-auto mt-4">
                      <Checkbox
                        size="small"
                        checked={deleteArtifacts}
                        onClick={() => setDeleteArtifacts(!deleteArtifacts)}
                      />
                    </div>
                    <span>
                      Also delete the{' '}
                      {maybePluralize(outputArtifactsCount, 'artifact')} this
                      run created. Any references to{' '}
                      {outputArtifactsCount > 1 ? 'these' : 'this'}{' '}
                      {maybePluralizeWord(outputArtifactsCount, 'artifact')}{' '}
                      will be broken.
                    </span>
                  </div>
                  {deleteArtifacts && (
                    <Banner variant="error" className="mt-16">
                      <BannerFlexWrapper>
                        <div>
                          <Icon name="warning" />
                        </div>
                        <BannerTextWrapper>
                          Any links from these artifacts to registries will be
                          deleted. References to linked versions will also be
                          broken.
                        </BannerTextWrapper>
                      </BannerFlexWrapper>
                    </Banner>
                  )}
                </div>
              )}
            </Dialog.Description>
            <div className="mb-8 mt-24 flex items-center">
              <Button
                variant="destructive"
                onClick={() => {
                  onDelete(deleteArtifacts);
                  setOpen(false);
                }}>
                {activeRunsCount > 0 ? `Yes, delete` : `Delete`}
              </Button>
              <Dialog.Close className="ml-8" asChild>
                <Button variant="ghost" ml={4}>
                  Cancel
                </Button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </React.Fragment>
  );
};

export const DeleteRunsButton = memo(DeleteRunsButtonComp);
